/**
 * The upload manager's whole part in cancellation: one `AbortController` per
 * in-flight item.
 *
 * The signal is the only channel cancellation crosses the transport seam on, so
 * the manager never learns anything transport-specific — no part numbers, no
 * upload ids, no multipart session state — and issues no transport call of its
 * own. A transport that owns server-side state releases it when the signal
 * fires; one that owns none simply stops.
 */
const controllers = new Map<string, AbortController>()

/** Opens a cancellation channel for one item and returns the signal to hand the transport. */
export function openCancellation(id: string): AbortSignal {
	const controller = new AbortController()
	controllers.set(id, controller)
	return controller.signal
}

/** Drops an item's channel once its upload has settled, successfully or not. */
export function closeCancellation(id: string): void {
	controllers.delete(id)
}

/**
 * Cancels an in-flight item. Returns `false` when there is nothing in flight
 * for that id — the item may still be queued, which the caller handles, or it
 * may already be finished, in which case cancelling is a no-op.
 */
export function requestCancellation(id: string): boolean {
	const controller = controllers.get(id)
	if (!controller) return false
	controller.abort()
	return true
}

/** Test-only: drop every channel so one spec's cancellations cannot leak into the next. */
export function resetCancellations(): void {
	controllers.clear()
}
